import { UserData, PlanType } from '../redux/slices/userSlice';

interface PlanChangeValidation {
  canChange: boolean;
  message: string;
  requiredActions?: string[];
}

/**
 * Verifica se é possível fazer downgrade do plano atual
 * @param userData Dados do usuário
 * @param targetPlan Plano de destino
 * @returns Objeto com resultado da validação
 */
export const validatePlanDowngrade = (userData: UserData, targetPlan: PlanType): PlanChangeValidation => {
  const currentPlan = userData.plan;
  const currentFeatures = userData.planFeatures;
  const stats = userData.stats;

  // Não permite downgrade durante período de trial
  if (currentFeatures.isTrial) {
    return {
      canChange: false,
      message: 'Não é possível fazer downgrade durante o período de teste.'
    };
  }

  // Verifica limites do plano de destino
  const targetFeatures = getPlanFeatures(targetPlan);
  const validationIssues: string[] = [];

  // Verifica limite de leads
  if (targetFeatures.maxLeads !== -1 && stats.activeLeads > targetFeatures.maxLeads) {
    validationIssues.push(`Remova ${stats.activeLeads - targetFeatures.maxLeads} leads para fazer o downgrade`);
  }

  // Verifica limite de produtos
  if (targetFeatures.maxProducts !== -1 && stats.totalProducts > targetFeatures.maxProducts) {
    validationIssues.push(`Remova ${stats.totalProducts - targetFeatures.maxProducts} produtos para fazer o downgrade`);
  }

  // Verifica usuários múltiplos
  if (!targetFeatures.multipleUsers && stats.activeUsers > 1) {
    validationIssues.push('Remova os usuários adicionais para fazer o downgrade');
  }

  if (validationIssues.length > 0) {
    return {
      canChange: false,
      message: 'Algumas ações são necessárias antes de fazer o downgrade:',
      requiredActions: validationIssues
    };
  }

  return {
    canChange: true,
    message: 'Downgrade permitido para o plano selecionado.'
  };
};

/**
 * Verifica se é possível fazer upgrade do plano atual
 * @param userData Dados do usuário
 * @param targetPlan Plano de destino
 * @returns Objeto com resultado da validação
 */
export const validatePlanUpgrade = (userData: UserData, targetPlan: PlanType): PlanChangeValidation => {
  const currentPlan = userData.plan;

  // Não permite upgrade para o mesmo plano
  if (currentPlan === targetPlan) {
    return {
      canChange: false,
      message: 'Você já está no plano selecionado.'
    };
  }

  // Não permite upgrade inválido (ex: Free -> Pro pulando o Basic)
  if (currentPlan === 'Free' && targetPlan === 'Pro') {
    return {
      canChange: false,
      message: 'Para acessar o plano Pro, primeiro faça upgrade para o plano Basic.'
    };
  }

  return {
    canChange: true,
    message: 'Upgrade permitido para o plano selecionado.'
  };
};

/**
 * Calcula o valor pro-rata para mudança de plano
 * @param userData Dados do usuário
 * @param targetPlan Plano de destino
 * @returns Valor a ser cobrado/reembolsado
 */
export const calculatePlanChangeValue = (userData: UserData, targetPlan: PlanType): number => {
  const currentFeatures = userData.planFeatures;
  const targetFeatures = getPlanFeatures(targetPlan);
  
  // Se está em trial ou plano gratuito, cobra o valor integral
  if (currentFeatures.isTrial || userData.plan === 'Free') {
    return targetFeatures.price;
  }

  // Se não houver data de fim do ciclo, retorna o valor integral
  if (!userData.currentCycleEnd) {
    return targetFeatures.price;
  }

  // Calcula dias restantes do ciclo atual
  const today = new Date();
  const cycleEndDate = new Date(userData.currentCycleEnd);
  const daysRemaining = Math.ceil((cycleEndDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const daysInMonth = 30;

  // Se a data de fim do ciclo já passou, cobra o valor integral
  if (daysRemaining <= 0) {
    return targetFeatures.price;
  }

  // Calcula valor proporcional do plano atual a ser reembolsado
  const currentRefund = (currentFeatures.price * daysRemaining) / daysInMonth;

  // Calcula valor proporcional do novo plano
  const newCharge = (targetFeatures.price * daysRemaining) / daysInMonth;

  // Retorna a diferença (positiva para upgrade, negativa para downgrade)
  return Number((newCharge - currentRefund).toFixed(2));
};

/**
 * Prepara os dados para a mudança de plano
 * @param userData Dados do usuário
 * @param targetPlan Plano de destino
 * @returns Objeto com dados preparados para a mudança
 */
export const preparePlanChange = (userData: UserData, targetPlan: PlanType) => {
  const targetFeatures = getPlanFeatures(targetPlan);
  const changeValue = calculatePlanChangeValue(userData, targetPlan);
  const today = new Date();
  
  // Se não houver data de fim do ciclo ou se ela já passou, define um novo ciclo
  const effectiveDate = today.toISOString();
  const nextBillingDate = userData.currentCycleEnd && new Date(userData.currentCycleEnd) > today
    ? calculateNextBillingDate(userData.currentCycleEnd)
    : calculateNextBillingDate(effectiveDate);
  
  return {
    planType: targetPlan,
    features: {
      ...targetFeatures,
      support: targetFeatures.support as 'basic' | 'priority' | '24/7'
    },
    proRataValue: changeValue,
    effectiveDate,
    nextBillingDate,
  };
};

/**
 * Calcula a próxima data de cobrança
 * @param currentDate Data atual ou data de fim do ciclo
 * @returns Nova data de cobrança
 */
export const calculateNextBillingDate = (currentDate: string): string => {
  const nextDate = new Date(currentDate);
  nextDate.setMonth(nextDate.getMonth() + 1);
  return nextDate.toISOString();
};

// Função auxiliar para obter features do plano
const getPlanFeatures = (plan: PlanType) => {
  return {
    maxLeads: plan === 'Pro' ? -1 : plan === 'Basic' ? 200 : 50,
    maxProducts: plan === 'Pro' ? -1 : plan === 'Basic' ? 50 : 10,
    whatsappBot: plan !== 'Free',
    emailMarketing: plan !== 'Free',
    chatIA: plan === 'Pro',
    multipleUsers: plan === 'Pro',
    customReports: plan !== 'Free',
    support: (plan === 'Pro' 
      ? '24/7' 
      : plan === 'Basic' 
        ? 'priority' 
        : 'basic') as 'basic' | 'priority' | '24/7',
    isTrial: false,
    trialDays: 0,
    price: plan === 'Pro' ? 99.90 : plan === 'Basic' ? 59.90 : 0,
    description: plan === 'Pro' 
      ? 'Plano profissional com recursos avançados'
      : plan === 'Basic'
        ? 'Plano básico para pequenos negócios'
        : 'Plano gratuito com 15 dias de acesso completo'
  };
}; 